import { Component, AfterViewInit, ViewChild, Output, Input, EventEmitter, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { DxDateBoxComponent, DxDateBoxModule, DxValidatorComponent, DxValidatorModule } from 'devextreme-angular';
import { DateTimeService } from '../datetime/datetime.service';
import { Timespan } from '../utils';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';

@Component({
    templateUrl: './datetime-picker.component.html',
    selector: 'ep-datetime-picker',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDateBoxModule, DxValidatorModule, DxiValidationRuleModule]
})
export class DatetimePickerComponent implements AfterViewInit, OnChanges {

    constructor(public dateTimeService: DateTimeService) { this.validationCallback = this.validationCallback.bind(this);  }

    @ViewChild('datepicker', { static: true }) datepicker: DxDateBoxComponent;
    @ViewChild('validator', { static: true }) validator: DxValidatorComponent;

    @Input() type: string = 'date';
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() readonly: boolean;
    @Input() timeInterval: number = 30;
    @Input() selectedDatetime: Date;
    @Input() selectedTime: string;
    @Input() required: boolean = false;
    @Input() validateSelectedTime: (e: Date) => boolean;
    
    @Output() selectedDatetimeChange: EventEmitter<Date> = new EventEmitter<Date>();
    @Output() selectedTimeChange: EventEmitter<string> = new EventEmitter<string>();

    // Review: These set/change values are not pretty. But they are used to prevent loops and to prevent the need for a re-render (detectChanges).
    public setSelectedDateInternal = false;
    public valueSetWithoutDatepickerInstance = false;
    public internalChanges = false;
    public format: any;

    ngAfterViewInit(): void {
        this.datepicker.instance.option('onOpened', (e) => {
            let value = this.datepicker.instance.option('value') as Date;
            if (!value) {
                value = new Date();
                value.setHours(0);
                value.setMinutes(0);
                value.setSeconds(0);
                this.setSelectedDate(value);
                this.selectedDatetimeChange.emit(value);
            }
        });

        if (this.selectedDatetime && this.valueSetWithoutDatepickerInstance) { // If the value has been set in the ngOnChanges - then the devexpress-datepicker instance is not yet available - therefore set the value here.
            this.valueSetWithoutDatepickerInstance = false;
            this.setSelectedDate(this.selectedDatetime);
        }
    }

    ngOnChanges(changes: any) {
        if (!this.internalChanges) { // Only listen for "outside" changes...
            if (changes.selectedDatetime && this.selectedDatetime && (this.type === 'date' || this.type == 'datetime')) {
                if (typeof this.selectedDatetime === 'string') {
                    this.selectedDatetime = new Date(this.selectedDatetime);
                }

                this.setSelectedDate(this.selectedDatetime);
            }
            else if (changes.selectedDatetime) {
                this.reset();
            }

            if (changes.selectedTime && this.selectedTime && this.type === 'time') {
                this.selectedDatetime = Timespan.convertTimeStringToDate(this.selectedTime);
                this.setSelectedDate(this.selectedDatetime);
            }
        }

        if (this.type === 'date') {
            this.format = this.dateTimeService.getDevExpressDateFunction;
        }
        if (this.type === 'datetime') {
            this.format = this.dateTimeService.getDevExpressDateTimeFunction;
        }
        if (this.type === 'time'){
            this.format = this.dateTimeService.formatTime;
        }

        this.internalChanges = false;
        this.validator?.instance?.validate();
    }

    reset() {
        if (this.datepicker.instance) {
            this.setSelectedDateInternal = true;
            this.datepicker.instance.reset();
            this.setSelectedDateInternal = false;
        }
        this.setSelectedDate(undefined);
        this.validator?.instance?.validate();
    }

    setSelectedDate(datetime: Date) {
        if (this.datepicker.instance) {
            this.setSelectedDateInternal = true;
            this.datepicker.instance.option('value', datetime);
            this.setSelectedDateInternal = false;
        }
        else {
            this.valueSetWithoutDatepickerInstance = true;
        }
    }

    dateChanged(e: any) {
        this.internalChanges = true;

        if (!this.setSelectedDateInternal) { // Only emit changes if it's a date chosen by the datepicker component.
            this.selectedDatetime = e.value;
            this.selectedDatetimeChange.emit(this.selectedDatetime);

            this.selectedTime = this.selectedDatetime ? Timespan.convertToTimeStringFromDate(this.selectedDatetime) : undefined;
            this.selectedTimeChange.emit(this.selectedTime);
        }

        this.validator?.instance?.validate();

        this.setSelectedDateInternal = false;
        this.internalChanges = false;
    }

    validationCallback(e: any): boolean {
        // If empty, let "required" rule handle it
        if (!e || e.value === null) return true;

        return this.validateSelectedTime ? this.validateSelectedTime(e.value) : true;
    }
}
